/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Notification, NotificationBuilder, NotificationType, UserLimit } from '@ca/core/models';
import { getNewDateFormatted } from '@ca/utils';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { toFileSize } from '../../../shared/pipes/file-size/file-size.pipe';

export class LimitsNotificationBuilder implements NotificationBuilder {
    type: NotificationType;
    private userLimit: UserLimit;
    private threshold: number;

    constructor(private translateService: TranslateService) {}

    setUserLimit(value: UserLimit): this {
        this.userLimit = value;
        return this;
    }

    setType(value: NotificationType): this {
        this.type = value;
        return this;
    }

    setThreshold(value: number): this {
        this.threshold = value;
        return this;
    }

    getType(): NotificationType {
        return this.type;
    }

    build(): Notification {
        const title: string = `${this.translateService.instant(
            this.type === 'computational_time' ? 'NOTIFICATIONS.COMPUTATIONAL_TIME' : 'NOTIFICATIONS.STORAGE',
        )} - ${this.translateService.instant(this.userLimit.usage >= this.userLimit.limit ? 'NOTIFICATIONS.EXCEEDED' : 'NOTIFICATIONS.RUNNING_OUT')}`;

        const text: string =
            this.userLimit.usage >= this.userLimit.limit
                ? Object.values(
                      this.translateService.instant(
                          this.type === 'computational_time'
                              ? ['NOTIFICATIONS.COMPUTATIONAL_TIME_EXCEEDED', 'NOTIFICATIONS.COMPUTATIONAL_TIME_EXCEEDED_DESC']
                              : ['NOTIFICATIONS.STORAGE_EXCEEDED', 'NOTIFICATIONS.STORAGE_EXCEEDED_DESC'],
                          {
                              // we should not be able to reach 31 days, since it is reset after 30. So compare all extensions, to this.
                              // Due to difference in hours (ex: 30days + 2 hours etc., exact 30 days will cause negative time to appear.)
                              date: getNewDateFormatted('DD.MM.YYYY', dayjs(this.userLimit.lastReset).add(31, 'day').format()),
                          },
                      ) || {},
                  ).join(' ')
                : this.type === 'computational_time'
                  ? `${this.userLimit.usage} / ${this.userLimit.limit} min. - ${this.translateService.instant('NOTIFICATIONS.RESET_DATE', {
                        date: getNewDateFormatted('DD.MM.YYYY', dayjs(this.userLimit.lastReset).add(31, 'day').format()),
                    })}`
                  : `${toFileSize(this.userLimit.usage)} / ${toFileSize(this.userLimit.limit)}`;

        const notification: Notification = {
            title,
            text,
            severity: this.userLimit.usage >= this.userLimit.limit ? 'alert' : 'warn',
            type: this.type,
            createdAt: new Date(),
        };
        return notification;
    }

    shouldBuildNotification(): boolean {
        return (this.userLimit.usage * 100) / this.userLimit.limit >= this.threshold;
    }
}
